/**
 * DomainCenterPage — 收件域（docs/ui_page_head_detail 主从布局）
 * 左栏：收件域选择器（DomainSidebar，含新建/编辑/删除）
 * 右栏：选中收件域下的 Tab（邮箱账号 / 别名与转发 / DKIM签名 / TLS安全 / SMTP收信设置）
 * 将原 DKIM、TLS、SMTP、邮箱账号、别名与转发 5 个独立菜单合并到本页，由域名驱动。
 */
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  TabList,
  Tab,
  mergeClasses,
} from '@fluentui/react-components'
import {
  GlobeRegular,
  MailArrowForwardRegular,
  ShieldRegular,
  LockClosedRegular,
  SettingsRegular,
  PeopleRegular,
  ServerRegular,
  EditRegular,
  DeleteRegular,
  SendRegular,
  RocketRegular,
} from '@fluentui/react-icons'
import { domainsApi } from '@/api/domains'
import type { Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import DomainSidebar from '@/components/DomainSidebar'
import DomainUsersTab from '@/pages/domain/DomainUsersTab'
import DomainAliasesTab from '@/pages/domain/DomainAliasesTab'
import DomainDkimTab from '@/pages/domain/DomainDkimTab'
import DomainTlsTab from '@/pages/domain/DomainTlsTab'
import DomainSmtpTab from '@/pages/domain/DomainSmtpTab'
import DomainMailTestTab from '@/pages/domain/DomainMailTestTab'
import DomainSendLogsTab from '@/pages/domain/DomainSendLogsTab'
import DomainServerConfigTab from '@/pages/domain/DomainServerConfigTab'

type TabKey = 'users' | 'aliases' | 'dkim' | 'tls' | 'smtp' | 'server' | 'mailtest' | 'sendlogs'

const VALID_TABS: TabKey[] = ['users', 'aliases', 'dkim', 'tls', 'smtp', 'server', 'mailtest', 'sendlogs']

interface DomainFormState {
  name: string
  description: string
  max_quota_bytes: string
  max_users: string
}

const EMPTY_FORM: DomainFormState = {
  name: '',
  description: '',
  max_quota_bytes: '',
  max_users: '',
}

const useStyles = makeStyles({
  root: { padding: '8px 12px', height: '100%', boxSizing: 'border-box' },
  inner: { height: '100%', display: 'flex', flexDirection: 'column' },
  split: { display: 'flex', gap: '16px', alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'hidden', padding: '2px 0 2px 2px' },
  right: { flex: '1 1 auto', minWidth: 0, height: '100%', boxSizing: 'border-box' },
  rightRoot: { display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' },
  rightHeader: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flexShrink: 0 },
  headerTitle: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  headerName: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
  headerSub: { fontFamily: 'var(--font-monospace, monospace)', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tabBar: {
    flexShrink: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    margin: '10px 0 12px',
    overflowX: 'auto',
    overflowY: 'hidden',
    // 横向滚动条默认隐藏，滚动中 / 悬浮容器时显示（docs/ui_scroller）
    '::-webkit-scrollbar': { height: '8px', width: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
    ':hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    ':hover::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' },
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    ':hover': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
  },
  // 滚动进行中：显示滚动条（由 scroll 事件 + debounce 控制，见 handleTabScroll）
  tabBarScrolling: {
    '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    scrollbarColor: 'rgba(0,0,0,0.25) transparent',
  },
  tabContent: { flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', padding: '0 2px 8px 2px' },
  rightPlaceholder: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: tokens.colorNeutralForeground3, padding: '40px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
})

export default function DomainCenterPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.list(),
  })

  const [searchParams, setSearchParams] = useSearchParams()

  // 从 URL 恢复选中域名与 Tab，便于分享/书签（如 ?domain=5&tab=tls）
  const initDomain = (() => {
    const v = Number(searchParams.get('domain'))
    return Number.isFinite(v) && v > 0 ? v : null
  })()
  const initTab = (() => {
    const v = searchParams.get('tab') as TabKey | null
    return v && VALID_TABS.includes(v) ? v : 'users'
  })()

  const [selectedId, setSelectedId] = useState<number | null>(initDomain)
  const [tab, setTab] = useState<TabKey>(initTab)

  // 状态 → URL 同步：把当前选中域名与 Tab 写入地址栏（replace，不堆积历史）
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (selectedId != null) next.set('domain', String(selectedId))
    else next.delete('domain')
    if (tab !== 'users') next.set('tab', tab)
    else next.delete('tab')
    setSearchParams(next, { replace: true })
  }, [selectedId, tab, setSearchParams])

  // 重新选择域名时回到首个 Tab
  const handleSelectDomain = (id: number) => {
    setSelectedId(id)
    setTab('users')
  }

  // 当前 Tab 超出可视宽度时，自动滚动到可见位置（含 URL 深链进入场景）
  const tabBarRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const active = tabBarRef.current?.querySelector('[role="tab"][aria-selected="true"]')
    active?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
  }, [tab, selectedId])

  // 滚动条显隐：滚动进行中短暂显示（debounce 400ms 后隐藏）；悬浮容器时由 CSS :hover 显示（docs/ui_scroller）
  const [tabScrolling, setTabScrolling] = useState(false)
  const tabScrollTimer = useRef<number | undefined>(undefined)
  const handleTabScroll = () => {
    setTabScrolling(true)
    if (tabScrollTimer.current !== undefined) window.clearTimeout(tabScrollTimer.current)
    tabScrollTimer.current = window.setTimeout(() => setTabScrolling(false), 400)
  }
  // 卸载时清理滚动 debounce 定时器
  useEffect(() => () => {
    if (tabScrollTimer.current !== undefined) window.clearTimeout(tabScrollTimer.current)
  }, [])

  const selectedDomain: Domain | undefined = selectedId != null
    ? domains.find((d) => d.id === selectedId)
    : undefined

  // 选中域名被删除时清空
  useEffect(() => {
    if (selectedId != null && !domains.some((d) => d.id === selectedId)) {
      setSelectedId(null)
    }
  }, [domains, selectedId])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['domains'] })
    queryClient.invalidateQueries({ queryKey: ['users'] })
    queryClient.invalidateQueries({ queryKey: ['aliases'] })
    queryClient.invalidateQueries({ queryKey: ['dkim'] })
  }

  // ── 新建域 ──
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<DomainFormState>(EMPTY_FORM)
  const openCreate = () => {
    setForm(EMPTY_FORM)
    setCreateOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string
      description?: string
      max_quota_bytes?: number
      max_users?: number
    }) => domainsApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setCreateOpen(false)
      setSelectedId(res.id)
      setTab('users')
      toast.success('创建成功', '收件域已创建')
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '创建失败，请稍后重试'
      toast.error('创建失败', msg)
    },
  })

  const handleCreate = () => {
    const name = form.name.trim()
    if (!name) {
      toast.error('请填写域名')
      return
    }
    createMutation.mutate({
      name,
      description: form.description.trim() || undefined,
      max_quota_bytes: form.max_quota_bytes.trim() ? Number(form.max_quota_bytes) : undefined,
      max_users: form.max_users.trim() ? Number(form.max_users) : undefined,
    })
  }

  // ── 编辑域 ──
  const [editTarget, setEditTarget] = useState<Domain | null>(null)
  const [editForm, setEditForm] = useState<DomainFormState>(EMPTY_FORM)
  const openEdit = (d: Domain) => {
    setEditTarget(d)
    setEditForm({
      name: d.name,
      description: d.description ?? '',
      max_quota_bytes: d.max_quota_bytes != null ? String(d.max_quota_bytes) : '',
      max_users: d.max_users != null ? String(d.max_users) : '',
    })
  }

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editTarget) return
      await domainsApi.update(editTarget.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        max_quota_bytes: editForm.max_quota_bytes.trim() ? Number(editForm.max_quota_bytes) : null,
        max_users: editForm.max_users.trim() ? Number(editForm.max_users) : null,
        default_forward_email: editTarget.defaultForwardEmail,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setEditTarget(null)
      toast.success('保存成功', '收件域信息已更新')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 删除域 ──
  const [deleteTarget, setDeleteTarget] = useState<Domain | null>(null)
  const deleteMutation = useMutation({
    mutationFn: async (d: Domain) => domainsApi.delete(d.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setDeleteTarget(null)
      if (selectedId === deleteTarget?.id) setSelectedId(null)
      toast.success('删除成功', '收件域已删除')
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  const countLabel = (d: Domain) => d.description || '收件域'

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.split}>
          {/* ── 左栏：收件域选择器 ── */}
          <DomainSidebar
            domains={domains}
            loading={isLoading}
            selectedId={selectedId}
            onSelect={handleSelectDomain}
            onNew={openCreate}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onRefresh={refresh}
            countLabel={countLabel}
            emptyHint="暂无收件域，点击左上角「+」新建"
          />

          {/* ── 右栏：选中收件域下的 Tab ── */}
          <div className={styles.right}>
            {selectedDomain ? (
              <div className={styles.rightRoot}>
                <div className={styles.rightHeader}>
                  <div className={styles.headerTitle}>
                    <span className={styles.headerName}>
                      <GlobeRegular />
                      {selectedDomain.name}
                      <Badge appearance="filled" color={selectedDomain.active ? 'success' : 'subtle'} size="small">
                        {selectedDomain.active ? '活跃' : '停用'}
                      </Badge>
                    </span>
                    <span className={styles.headerSub}>
                      {selectedDomain.description || '收件域'} · 该域名的全部配置集中在本页
                    </span>
                  </div>
                  <Button appearance="subtle" size="small" icon={<EditRegular />} onClick={() => openEdit(selectedDomain)}>编辑</Button>
                </div>

                <div
                  className={mergeClasses(styles.tabBar, tabScrolling && styles.tabBarScrolling)}
                  ref={tabBarRef}
                  onScroll={handleTabScroll}
                >
                  <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as TabKey)}>
                    <Tab value="users" icon={<PeopleRegular />}>邮箱账号</Tab>
                    <Tab value="aliases" icon={<MailArrowForwardRegular />}>别名与转发</Tab>
                    <Tab value="dkim" icon={<ShieldRegular />}>DKIM 签名</Tab>
                    <Tab value="tls" icon={<LockClosedRegular />}>TLS 安全</Tab>
                    <Tab value="smtp" icon={<SettingsRegular />}>SMTP/收信设置</Tab>
                    <Tab value="server" icon={<ServerRegular />}>服务器配置</Tab>
                    <Tab value="mailtest" icon={<SendRegular />}>写信发送</Tab>
                    <Tab value="sendlogs" icon={<RocketRegular />}>发送记录</Tab>
                  </TabList>
                </div>

                <div className={styles.tabContent}>
                  {tab === 'users' && <DomainUsersTab domain={selectedDomain} />}
                  {tab === 'aliases' && <DomainAliasesTab domain={selectedDomain} />}
                  {tab === 'dkim' && <DomainDkimTab domain={selectedDomain} />}
                  {tab === 'tls' && <DomainTlsTab domain={selectedDomain} />}
                  {tab === 'smtp' && <DomainSmtpTab domain={selectedDomain} />}
                  {tab === 'server' && <DomainServerConfigTab domain={selectedDomain} />}
                  {tab === 'mailtest' && <DomainMailTestTab domain={selectedDomain} />}
                  {tab === 'sendlogs' && <DomainSendLogsTab domain={selectedDomain} />}
                </div>
              </div>
            ) : (
              <div className={styles.rightPlaceholder}>
                <GlobeRegular style={{ fontSize: '40px' }} />
                <Text>从左侧选择一个收件域，集中管理该域名的邮箱、别名、DKIM、TLS 与收信设置</Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 新建收件域 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="新建收件域" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。创建后立即生效，并可在本页右侧配置该域名的邮箱/别名/DKIM/TLS。">域名</InfoLabel>} required>
                  <Input value={form.name} onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={form.description} onChange={(_, d) => setForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域下所有用户的总配额上限，留空表示不限。">最大配额（字节）</InfoLabel>} hint="留空表示不限">
                  <Input type="number" value={form.max_quota_bytes} onChange={(_, d) => setForm((f) => ({ ...f, max_quota_bytes: d.value }))} placeholder="可选" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域可创建的最大用户数，留空表示不限。">最大用户数</InfoLabel>} hint="留空表示不限">
                  <Input type="number" value={form.max_users} onChange={(_, d) => setForm((f) => ({ ...f, max_users: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 编辑收件域 */}
      <Dialog open={!!editTarget} onOpenChange={(_, d) => { if (!d.open) setEditTarget(null) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="编辑收件域" onClose={() => setEditTarget(null)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="收件域 / 投递域的完整域名，例如 example.com。">域名</InfoLabel>} required>
                  <Input value={editForm.name} onChange={(_, d) => setEditForm((f) => ({ ...f, name: d.value }))} placeholder="例如 example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域的用途说明，仅作管理备注。">描述</InfoLabel>}>
                  <Input value={editForm.description} onChange={(_, d) => setEditForm((f) => ({ ...f, description: d.value }))} placeholder="可选" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域下所有用户的总配额上限，留空表示不限。">最大配额（字节）</InfoLabel>} hint="留空表示不限">
                  <Input type="number" value={editForm.max_quota_bytes} onChange={(_, d) => setEditForm((f) => ({ ...f, max_quota_bytes: d.value }))} placeholder="可选" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="该域可创建的最大用户数，留空表示不限。">最大用户数</InfoLabel>} hint="留空表示不限">
                  <Input type="number" value={editForm.max_users} onChange={(_, d) => setEditForm((f) => ({ ...f, max_users: d.value }))} placeholder="可选" />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => editMutation.mutate()} disabled={editMutation.isPending}>
                {editMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认 */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null) }} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="删除收件域" onClose={() => setDeleteTarget(null)} />
            <DialogContent>
              <Text>确定要删除收件域 <b>{deleteTarget?.name}</b> 吗？该域名下的邮箱、别名等配置将一并影响，此操作不可恢复。</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={() => setDeleteTarget(null)}>取消</Button>
              <Button appearance="primary" icon={<DeleteRegular />} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? '删除中…' : '删除'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}